import { computed, inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IOpeningTimeInfo } from '../../models';
import { DateService } from '../date/date.service';
import { EnvironmentService } from '../environment/environment.service';

/** Calculated opening-time status for a concrete point in time. */
export interface IOpeningTimesStatus {
  /** True when the location is currently open. */
  isOpen: boolean;
  /** The currently active opening-time entry (if open). */
  activeEntry?: IOpeningTimeInfo;
  /** Indicates whether special opening times were used for this calculation. */
  usesSpecialOpeningTimes: boolean;
}

/** Special opening-time entry enriched with parsed date objects. */
export interface IUpcomingSpecialOpeningTime extends IOpeningTimeInfo {
  /** Parsed `dateFrom` — guaranteed present. */
  startDate: Date;
  /** Parsed `dateTo` — undefined for single-day entries. */
  endDate: Date | undefined;
}

/** Single sub-entry inside an `ISpecialOpeningTimeGroup`, enriched with a localized range string. */
export interface ISpecialOpeningTimeEntry extends IUpcomingSpecialOpeningTime {
  /** Localized opening-range string, or empty for closed/invalid entries. */
  formattedRange: string;
}

/**
 * Group of special opening-time entries that share the same `openingTimeName`.
 * Backends often emit one entry per weekday block (Mo–Fr, Sat) for the same conceptual
 * period; grouping renders them as a single block with one shared title and date range.
 */
export interface ISpecialOpeningTimeGroup {
  /** Display label — `openingTimeName` if set, otherwise the first entry's `title`. */
  label: string;
  /** Earliest start date across the group. */
  startDate: Date;
  /** Latest end date across the group, or `undefined` for single-day groups. */
  endDate: Date | undefined;
  /** Sub-entries belonging to this group, sorted by `index` then start date. */
  entries: ISpecialOpeningTimeEntry[];
}

/** Default look-ahead window for upcoming special opening times (in days). */
const DEFAULT_UPCOMING_WINDOW_DAYS = 90;

@Injectable({
  providedIn: 'root'
})
export class OpeningTimesService {
  // Services
  /** Shared date helper for range and validity checks. */
  private readonly dateService = inject(DateService);
  /** Environment access for the `USE_VISITOR_TIME_ZONE` toggle. */
  private readonly environmentService = inject(EnvironmentService);
  /** Translation service for localized opening-time texts. */
  private readonly translateService = inject(TranslateService);

  // Properties & Signals
  /** Regular weekly opening times provided by the backend. */
  private readonly regularOpeningTimes: WritableSignal<IOpeningTimeInfo[]> = signal([]);
  /** Special opening times that override the regular schedule for matching days. */
  private readonly specialOpeningTimes: WritableSignal<IOpeningTimeInfo[]> = signal([]);
  /** Reference date used by the public computed signals. */
  private readonly referenceDate: WritableSignal<Date> = signal(new Date());
  /** Store timezone (IANA name) used for UTC→local conversion. Set by caller from companyInfo/chainstore. */
  private readonly storeTimeZone: WritableSignal<string | undefined> = signal(undefined);

  /**
   * Calculated status for the current `referenceDate`.
   * Contains whether the location is open, the active time entry and whether the result
   * came from special opening times.
   */
  readonly status: Signal<IOpeningTimesStatus> = computed(() => {
    return this.calculateStatus(this.referenceDate());
  });
  /**
   * Localized open/closed label for the current `referenceDate`.
   * Returns e.g. "Geöffnet" / "Geschlossen" (de), "Open" / "Closed" (en).
   */
  readonly openOrClosedText: Signal<string> = computed(() => {
    return this.translateService.instant(this.status().isOpen ? 'openingTimes.open' : 'openingTimes.closed');
  });
  /**
   * Localized display text for the first opening range on the reference date.
   * Example: "Heute geöffnet von 08:00 - 18:00" or "Tomorrow open from 08:00 - 18:00".
   */
  readonly todaysOpeningTime: Signal<string> = computed(() => {
    return this.getTodaysOpeningTimeText(this.referenceDate());
  });
  /**
   * Localized text for the next relevant state change.
   * Example: "Schließt um 18:00" (de) / "Closes at 18:00" (en) while open.
   */
  readonly todaysOpeningOrClosingTime: Signal<string> = computed(() => {
    return this.getTodaysOpeningOrClosingTimeText(this.referenceDate());
  });

  // Methods
  /**
   * Sets regular and special opening times used by this service.
   * @param regularOpeningTimes Regular opening times.
   * @param specialOpeningTimes Special opening times with higher priority.
   */
  public setOpeningTimes(
    regularOpeningTimes: IOpeningTimeInfo[] | null | undefined,
    specialOpeningTimes: IOpeningTimeInfo[] | null | undefined
  ): void {
    this.regularOpeningTimes.set(regularOpeningTimes ?? []);
    this.specialOpeningTimes.set(specialOpeningTimes ?? []);
  }

  /**
   * Sets the reference date used by computed signals.
   * @param date Reference date for reactive calculations.
   */
  public setReferenceDate(date: Date): void {
    if (!DateService.isValidDate(date)) return;
    this.referenceDate.set(new Date(date));
  }

  /**
   * Sets the store IANA timezone (e.g. "Europe/Berlin") used for interpreting UTC opening times.
   * Pass `undefined` to fall back to the user's browser timezone.
   * @param timeZone IANA timezone name or undefined.
   */
  public setStoreTimeZone(timeZone: string | undefined): void {
    this.storeTimeZone.set(timeZone);
  }

  /**
   * Returns whether the location is open at the provided date.
   * @param date Date to evaluate.
   * @returns True if open, otherwise false.
   */
  public isOpen(date: Date = new Date()): boolean {
    return this.calculateStatus(date).isOpen;
  }

  /**
   * Returns "Geöffnet" or "Geschlossen" for the provided date.
   * @param date Date to evaluate.
   * @returns Localized open/closed text.
   */
  public getOpenOrClosedText(date: Date = new Date()): string {
    return this.translateService.instant(this.calculateStatus(date).isOpen ? 'openingTimes.open' : 'openingTimes.closed');
  }

  /**
   * Builds a localized opening-range text for the first opening entry of the given date.
   * Uses internal signal state for regular/special opening times.
   * @param date Date to evaluate.
   * @returns Localized text like "Heute geöffnet von 08:00 - 18:00" or "Morgen geschlossen".
   */
  public getTodaysOpeningTimeText(date: Date = new Date()): string {
    return this.getTodaysOpeningTimeTextForEntries(this.regularOpeningTimes(), this.specialOpeningTimes(), date);
  }

  /**
   * Whether opening times are maintained that can describe the given date.
   * Regular opening times always count; special opening times only count when one applies to the date,
   * so a single (or past) special entry does not cause a "closed" text on every other day.
   * Without such entries, the text would always fall back to "closed" — callers hide the output instead.
   * @param regularOpeningTimes Regular opening times.
   * @param specialOpeningTimes Special opening times.
   * @param date Date to evaluate.
   * @returns True when regular opening times exist or a special entry applies to the date.
   */
  public hasOpeningTimes(
    regularOpeningTimes: IOpeningTimeInfo[] | null | undefined,
    specialOpeningTimes: IOpeningTimeInfo[] | null | undefined,
    date: Date = new Date()
  ): boolean {
    return !!regularOpeningTimes?.length || this.getSpecialEntriesForDate(date, specialOpeningTimes ?? []).length > 0;
  }

  /**
   * Builds a localized opening-range text for the given date using explicitly provided arrays.
   * Does not read or write internal signals — safe to call from computed contexts.
   * @param regularOpeningTimes Regular opening times.
   * @param specialOpeningTimes Special opening times.
   * @param date Date to evaluate.
   * @returns Localized text like "Heute geöffnet von 08:00 - 18:00" or "Morgen geschlossen".
   */
  public getTodaysOpeningTimeTextForEntries(
    regularOpeningTimes: IOpeningTimeInfo[] | null | undefined,
    specialOpeningTimes: IOpeningTimeInfo[] | null | undefined,
    date: Date = new Date()
  ): string {
    return this.getOpeningTimeTextForDateFromEntries(
      this.getEntriesWithPriorityForDate(date, regularOpeningTimes ?? [], specialOpeningTimes ?? []),
      date
    );
  }

  /**
   * Formats an opening-time entry as a localized range string.
   * Produces `"08:00–18:00"` or, with break, `"08:00–12:00, 13:00–18:00"` (en dash without spaces).
   * Times are rendered in the store timezone, or the visitor's when none is set (see `renderTimeForDisplay`).
   * Returns an empty string for closed entries or entries without valid UTC timestamps.
   * @param entry Opening-time entry to format.
   * @returns Localized range string.
   */
  public formatEntryRange(entry: IOpeningTimeInfo | null | undefined): string {
    if (!entry || entry.closed) return '';

    const openFrom = this.renderTimeForDisplay(entry.openFrom, false);
    const openTo = this.renderTimeForDisplay(entry.openTo, false);
    if (!openFrom || !openTo) return '';

    const breakFrom = this.renderTimeForDisplay(entry.breakFrom, false);
    const breakTo = this.renderTimeForDisplay(entry.breakTo, false);
    if (breakFrom && breakTo) {
      return `${openFrom}–${breakFrom}, ${breakTo}–${openTo}`;
    }

    return `${openFrom}–${openTo}`;
  }

  /**
   * Normalizes a backend day label for display: day ranges use an en dash without spaces.
   * The backend emits `"Mo.-Fr."` or `"Mo. - Mi."`; both become `"Mo.–Fr."` / `"Mo.–Mi."`.
   * @param title Day label from the backend (`IOpeningTimeInfo.title`).
   * @returns Display-ready day label, or empty string when missing.
   */
  public formatDayLabel(title: string | null | undefined): string {
    return (title ?? '').trim().replace(/\s*[-–]\s*/g, '–');
  }

  /**
   * Formats an opening-time entry as a localized, display-ready range string.
   * Applies the locale's time suffix (e.g. `"Uhr"` in DE via `openingTimes.oclock`) and, in
   * visitor-TZ mode, appends the timezone label once behind it (e.g. `"08:00–18:00 Uhr MESZ"`).
   * @param entry Opening-time entry to format.
   * @returns Display-ready range string, or empty string for closed/invalid entries.
   */
  public formatEntryRangeLocalized(entry: IOpeningTimeInfo | null | undefined): string {
    if (!entry || entry.closed) return '';

    const range = this.formatEntryRange(entry);
    if (!range) return '';

    const text = this.translateService.instant('openingTimes.oclock', { time: range });
    const timeZoneLabel = this.getTimeZoneLabel(entry.openFrom);
    return timeZoneLabel ? `${text} ${timeZoneLabel}` : text;
  }

  /**
   * Returns special opening-time entries that are still relevant inside a look-ahead window,
   * with `dateFrom` / `dateTo` parsed into `Date` objects and sorted by start date.
   * Entries without a parsable `dateFrom` are dropped.
   * @param specialOpeningTimes Raw special opening times from the backend.
   * @param windowDays Look-ahead window in days from the reference date.
   * @param date Reference date (defaults to today).
   * @returns Filtered, enriched and sorted upcoming special opening times.
   */
  public getUpcomingSpecialOpeningTimes(
    specialOpeningTimes: IOpeningTimeInfo[] | null | undefined,
    windowDays: number = DEFAULT_UPCOMING_WINDOW_DAYS,
    date: Date = new Date()
  ): IUpcomingSpecialOpeningTime[] {
    const today = DateService.toStartOfDay(date);
    const windowEndDay = new Date(today);
    windowEndDay.setDate(today.getDate() + windowDays);
    const windowEnd = DateService.toEndOfDay(windowEndDay);

    return (specialOpeningTimes ?? [])
      .map((item) => ({
        ...item,
        startDate: DateService.parseBackendDate(item.dateFrom),
        endDate: DateService.parseBackendDate(item.dateTo)
      }))
      .filter((item): item is IUpcomingSpecialOpeningTime => {
        if (!item.startDate) return false;
        return DateService.toEndOfDay(item.endDate ?? item.startDate) >= today && DateService.toStartOfDay(item.startDate) <= windowEnd;
      })
      .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
  }

  /**
   * Groups upcoming special opening times by `openingTimeName`. Each group renders as a single
   * visible block with one shared label and date range; the per-day entries (e.g. "Mo.-Fr.",
   * "Sa.") are returned as sub-entries with their own formatted ranges.
   * Falls back to `title` when `openingTimeName` is missing.
   * @param specialOpeningTimes Raw special opening times from the backend.
   * @param windowDays Look-ahead window in days from the reference date.
   * @param date Reference date (defaults to today).
   * @returns Grouped special opening times, sorted by start date.
   */
  public getUpcomingSpecialOpeningTimeGroups(
    specialOpeningTimes: IOpeningTimeInfo[] | null | undefined,
    windowDays: number = DEFAULT_UPCOMING_WINDOW_DAYS,
    date: Date = new Date()
  ): ISpecialOpeningTimeGroup[] {
    const upcoming = this.getUpcomingSpecialOpeningTimes(specialOpeningTimes, windowDays, date);
    const groupsByKey = new Map<string, ISpecialOpeningTimeGroup>();

    for (const entry of upcoming) {
      const title = entry.title === entry.openingTimeName ? (entry.title ?? '') : this.formatDayLabel(entry.title);
      const label = (entry.openingTimeName || title).trim();
      const enrichedEntry: ISpecialOpeningTimeEntry = { ...entry, title, formattedRange: this.formatEntryRangeLocalized(entry) };

      const existing = groupsByKey.get(label);
      if (!existing) {
        groupsByKey.set(label, { label, startDate: entry.startDate, endDate: entry.endDate, entries: [enrichedEntry] });
        continue;
      }

      if (entry.startDate.getTime() < existing.startDate.getTime()) existing.startDate = entry.startDate;
      if (entry.endDate && (!existing.endDate || entry.endDate.getTime() > existing.endDate.getTime())) {
        existing.endDate = entry.endDate;
      }
      existing.entries.push(enrichedEntry);
    }

    for (const group of groupsByKey.values()) {
      group.entries.sort((a, b) => (a.index ?? 0) - (b.index ?? 0) || a.startDate.getTime() - b.startDate.getTime());
    }

    return Array.from(groupsByKey.values()).sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
  }

  /**
   * Builds the transition text for open/closed state.
   * Returns the closing time when open, or the next opening time when closed.
   * @param date Date to evaluate.
   * @returns "Schließt um HH:mm" or "Öffnet um HH:mm", fallback "Geschlossen".
   */
  public getTodaysOpeningOrClosingTimeText(date: Date = new Date()): string {
    const closed = this.translateService.instant('openingTimes.closed');
    const currentStatus = this.calculateStatus(date);

    if (currentStatus.isOpen && currentStatus.activeEntry?.openTo) {
      const closeTime = this.renderTimeForDisplay(currentStatus.activeEntry.openTo);
      return closeTime ? this.translateService.instant('openingTimes.closesAt', { time: closeTime }) : closed;
    }

    const nextOpening = this.findNextOpeningEntry(date);
    if (!nextOpening?.openFrom) return closed;

    const openFrom = this.renderTimeForDisplay(nextOpening.openFrom);
    return openFrom ? this.translateService.instant('openingTimes.opensAt', { time: openFrom }) : closed;
  }

  /**
   * Builds a localized opening-range text for a specific date and preselected day entries.
   * @param entries Opening entries for the evaluated date.
   * @param date Evaluated date.
   * @returns Localized text for open range or closed state.
   */
  private getOpeningTimeTextForDateFromEntries(entries: IOpeningTimeInfo[], date: Date): string {
    const dayLabel = this.getDayLabel(date);
    const firstOpenEntry = this.getFirstOpeningEntry(entries);
    if (!firstOpenEntry) return this.translateService.instant('openingTimes.closedDay', { day: dayLabel });

    const openFrom = this.renderTimeForDisplay(firstOpenEntry.openFrom, false);
    const openTo = this.renderTimeForDisplay(firstOpenEntry.openTo, false);
    if (!openFrom || !openTo) return this.translateService.instant('openingTimes.closedDay', { day: dayLabel });

    // `openRange` already carries the locale's time suffix, so the TZ label goes behind the whole text.
    const text = this.translateService.instant('openingTimes.openRange', { day: dayLabel, from: openFrom, to: openTo });
    const timeZoneLabel = this.getTimeZoneLabel(firstOpenEntry.openFrom);
    return timeZoneLabel ? `${text} ${timeZoneLabel}` : text;
  }

  /**
   * Returns a localized day label for the evaluated date.
   * Uses "today" / "tomorrow" labels for relative dates, otherwise a localized absolute date.
   * @param date Evaluated date.
   * @returns Localized day label.
   */
  private getDayLabel(date: Date): string {
    const today = DateService.toStartOfDay(new Date());
    const compareDate = DateService.toStartOfDay(date);

    if (today.getTime() === compareDate.getTime()) return this.translateService.instant('openingTimes.today');

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (tomorrow.getTime() === compareDate.getTime()) return this.translateService.instant('openingTimes.tomorrow');

    const locale = this.translateService.getCurrentLang() ?? this.translateService.getFallbackLang() ?? 'de-DE';
    const formattedDate = new Intl.DateTimeFormat(locale).format(compareDate);
    return this.translateService.instant('openingTimes.onDate', { date: formattedDate });
  }

  /**
   * Calculates open/closed status at a specific date.
   * @param date Date to evaluate.
   * @returns Full status object.
   */
  public calculateStatus(date: Date = new Date()): IOpeningTimesStatus {
    const specialEntries = this.getSpecialEntriesForDate(date);
    const usesSpecialOpeningTimes = specialEntries.length > 0;
    const entries = usesSpecialOpeningTimes ? specialEntries : this.getRegularEntriesForDate(date);
    const activeEntry = entries.find((entry) => this.isEntryOpenAt(entry, date));

    return { isOpen: !!activeEntry, activeEntry, usesSpecialOpeningTimes };
  }

  /**
   * Selects entries for the given date — special opening times take priority over regular ones.
   * @param date Date to evaluate.
   * @returns Entries to be used for this day.
   */
  private getEntriesWithPriorityForDate(
    date: Date,
    regularOpeningTimes: IOpeningTimeInfo[] = this.regularOpeningTimes(),
    specialOpeningTimes: IOpeningTimeInfo[] = this.specialOpeningTimes()
  ): IOpeningTimeInfo[] {
    const specialEntries = this.getSpecialEntriesForDate(date, specialOpeningTimes);
    return specialEntries.length > 0 ? specialEntries : this.getRegularEntriesForDate(date, regularOpeningTimes);
  }

  /**
   * Returns special entries valid for the provided date, sorted ascending by opening time.
   * @param date Date to evaluate.
   * @returns Matching special entries.
   */
  private getSpecialEntriesForDate(date: Date, specialOpeningTimes: IOpeningTimeInfo[] = this.specialOpeningTimes()): IOpeningTimeInfo[] {
    const tz = this.getEffectiveTimeZone();
    return specialOpeningTimes
      .filter((entry) => this.matchesSpecialDate(entry, date))
      .sort((a, b) => this.getTimeInMinutesInZone(a.openFrom, tz) - this.getTimeInMinutesInZone(b.openFrom, tz));
  }

  /**
   * Returns regular entries matching the provided date's weekday, sorted ascending by opening time.
   * @param date Date to evaluate.
   * @returns Matching regular entries.
   */
  private getRegularEntriesForDate(date: Date, regularOpeningTimes: IOpeningTimeInfo[] = this.regularOpeningTimes()): IOpeningTimeInfo[] {
    const tz = this.getEffectiveTimeZone();
    return regularOpeningTimes
      .filter((entry) => this.matchesWeekday(entry, date))
      .sort((a, b) => this.getTimeInMinutesInZone(a.openFrom, tz) - this.getTimeInMinutesInZone(b.openFrom, tz));
  }

  /**
   * Checks whether a given entry covers the exact moment represented by date.
   * Considers break windows as closed. Uses store timezone for time-of-day extraction.
   * @param entry Opening-time entry to check.
   * @param date Moment to evaluate.
   * @returns True if the entry is open at that moment.
   */
  private isEntryOpenAt(entry: IOpeningTimeInfo, date: Date): boolean {
    if (entry.closed) return false;

    const tz = this.getEffectiveTimeZone();
    const openFrom = this.getTimeInMinutesInZone(entry.openFrom, tz);
    const openTo = this.getTimeInMinutesInZone(entry.openTo, tz);
    if (openFrom < 0 || openTo < 0) return false;

    const nowMinutes = this.getMinutesOfDayInZone(date, tz);
    if (nowMinutes < openFrom || nowMinutes >= openTo) return false;

    const breakFrom = this.getTimeInMinutesInZone(entry.breakFrom, tz);
    const breakTo = this.getTimeInMinutesInZone(entry.breakTo, tz);
    if (breakFrom >= 0 && breakTo >= 0 && nowMinutes >= breakFrom && nowMinutes < breakTo) return false;

    return true;
  }

  /**
   * Returns the first non-closed entry with valid opening times from the provided list.
   * @param entries Day entries to search.
   * @returns First opening entry or undefined.
   */
  private getFirstOpeningEntry(entries: IOpeningTimeInfo[]): IOpeningTimeInfo | undefined {
    const tz = this.getEffectiveTimeZone();
    return entries.find(
      (entry) => !entry.closed && this.getTimeInMinutesInZone(entry.openFrom, tz) >= 0 && this.getTimeInMinutesInZone(entry.openTo, tz) >= 0
    );
  }

  /**
   * Searches up to 14 days ahead for the next day that has an opening entry.
   * @param date Starting date.
   * @returns The first upcoming opening entry or undefined.
   */
  private findNextOpeningEntry(date: Date): IOpeningTimeInfo | undefined {
    const MAX_LOOKAHEAD_DAYS = 14;

    for (let offset = 1; offset <= MAX_LOOKAHEAD_DAYS; offset++) {
      const nextDate = new Date(date);
      nextDate.setDate(date.getDate() + offset);
      const firstEntry = this.getFirstOpeningEntry(this.getEntriesWithPriorityForDate(nextDate));
      if (firstEntry) return firstEntry;
    }

    return undefined;
  }

  /**
   * Checks if a special entry matches the provided date.
   * A special period often emits one entry per weekday block (Mo–Fr, Sa, Su), each carrying
   * the same `dateFrom`/`dateTo` range plus its own weekday config. Both must match: the date
   * has to fall in the range AND the entry's weekday has to match the evaluated day — otherwise
   * a "Mo–Fr" entry would wrongly count as open on a Sunday inside the range.
   * @param entry Special entry.
   * @param date Date to evaluate.
   * @returns True if it matches.
   */
  private matchesSpecialDate(entry: IOpeningTimeInfo, date: Date): boolean {
    const hasDateRange = !!(entry.dateFrom || entry.dateTo);
    const hasWeekdayConfig = !!(entry.daysOfWeek?.length || entry.workdays || entry.weekday || entry.saturday || entry.sunday);

    if (hasDateRange && !this.matchesDateRange(entry, date)) return false;
    if (hasWeekdayConfig && !this.matchesWeekday(entry, date)) return false;

    return hasDateRange || hasWeekdayConfig;
  }

  /**
   * Checks if the provided date falls within the entry's date range.
   * `dateFrom` and `dateTo` are expected as ISO date strings (e.g. "2026-03-15"),
   * which are parsed natively by `new Date()`.
   * @param entry Entry with dateFrom / dateTo.
   * @param date Date to evaluate.
   * @returns True if date is in range.
   */
  private matchesDateRange(entry: IOpeningTimeInfo, date: Date): boolean {
    const dateFrom = DateService.parseBackendDate(entry.dateFrom);
    const dateTo = DateService.parseBackendDate(entry.dateTo);

    if (!dateFrom && !dateTo) return false;

    const startDate = dateFrom ? DateService.toStartOfDay(dateFrom) : DateService.toStartOfDay(date);
    const endDate = dateTo ? DateService.toEndOfDay(dateTo) : DateService.toEndOfDay(startDate);

    if (entry.recurrent) return this.matchesRecurrentRange(startDate, endDate, date);

    return this.dateService.isBetween(startDate, endDate, date);
  }

  /**
   * Checks a yearly-recurring date range by projecting it onto the current and adjacent years.
   * Handles wrapping ranges that span across a year boundary (e.g. Dec 01 – Jan 06).
   * Uses store timezone to determine the calendar day of `date`.
   * @param startDate Range start — only month and day are used for recurrence.
   * @param endDate Range end — only month and day are used for recurrence.
   * @param date Date to evaluate.
   * @returns True if date falls within the recurring range.
   */
  private matchesRecurrentRange(startDate: Date, endDate: Date, date: Date): boolean {
    const tz = this.getEffectiveTimeZone();
    const parts = this.getDatePartsInZone(date, tz);
    const currentYear = parts.year;
    // Use noon to avoid DST edge cases when comparing at day boundaries
    const currentDate = new Date(currentYear, parts.month, parts.day, 12, 0, 0, 0);

    const startCurrentYear = new Date(currentYear, startDate.getMonth(), startDate.getDate(), 0, 0, 0, 0);
    const endCurrentYear = new Date(currentYear, endDate.getMonth(), endDate.getDate(), 23, 59, 59, 999);

    // Non-wrapping range (e.g. Jun 01 – Aug 31)
    if (startCurrentYear <= endCurrentYear) {
      return this.dateService.isBetween(startCurrentYear, endCurrentYear, currentDate);
    }

    // Wrapping range (e.g. Dec 25 – Jan 06) — check both direction across year boundary
    const endNextYear = new Date(currentYear + 1, endDate.getMonth(), endDate.getDate(), 23, 59, 59, 999);
    const startPrevYear = new Date(currentYear - 1, startDate.getMonth(), startDate.getDate(), 0, 0, 0, 0);

    return (
      this.dateService.isBetween(startCurrentYear, endNextYear, currentDate) ||
      this.dateService.isBetween(startPrevYear, endCurrentYear, currentDate)
    );
  }

  /**
   * Checks whether the entry's weekday configuration matches the weekday of the provided date.
   * Weekday is evaluated in the store timezone so that e.g. a user in Tokyo looking at
   * a Berlin shop at "Saturday 06:00 Tokyo = Friday 23:00 Berlin" still matches Friday entries.
   * Priority: daysOfWeek array → workdays/weekday flags → saturday → sunday.
   * @param entry Entry to test.
   * @param date Date to evaluate.
   * @returns True if weekday matches.
   */
  private matchesWeekday(entry: IOpeningTimeInfo, date: Date): boolean {
    const weekday = this.getWeekdayInZone(date, this.getEffectiveTimeZone()); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

    if (entry.daysOfWeek?.length) return entry.daysOfWeek.includes(weekday);
    if (entry.workdays || entry.weekday) return weekday >= 1 && weekday <= 5;
    if (entry.saturday) return weekday === 6;
    if (entry.sunday) return weekday === 0;

    return false;
  }

  /**
   * Formats a UTC ISO timestamp for display in the store timezone, falling back to the visitor's
   * timezone when no store timezone is set (see `getEffectiveTimeZone`). With `USE_VISITOR_TIME_ZONE`
   * enabled the visitor's timezone is used instead and a short label (e.g. "GMT+2") is appended
   * when it differs from the store timezone.
   * @param utcIso UTC ISO timestamp (e.g. "2026-07-15T06:00:00Z").
   * @param withTimeZoneLabel Whether the label may be appended — `false` for ranges, which carry it once at the end.
   * @returns Localized time string or undefined if input is invalid.
   */
  private renderTimeForDisplay(utcIso?: string, withTimeZoneLabel: boolean = true): string | undefined {
    if (!utcIso) return undefined;
    const date = new Date(utcIso);
    if (!DateService.isValidDate(date)) return undefined;

    const showLabel = withTimeZoneLabel && this.showsTimeZoneLabel();

    return new Intl.DateTimeFormat(this.getDisplayLocale(), {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: this.environmentService.useVisitorTimeZone ? undefined : this.getEffectiveTimeZone(),
      timeZoneName: showLabel ? 'short' : undefined
    }).format(date);
  }

  /**
   * Returns the short timezone label (e.g. "MESZ") for the given timestamp in visitor-TZ mode.
   * @param utcIso UTC ISO timestamp the label is resolved for (DST-dependent).
   * @returns Short timezone label, or undefined when no label is needed or the input is invalid.
   */
  private getTimeZoneLabel(utcIso?: string): string | undefined {
    if (!utcIso || !this.showsTimeZoneLabel()) return undefined;
    const date = new Date(utcIso);
    if (!DateService.isValidDate(date)) return undefined;

    const parts = new Intl.DateTimeFormat(this.getDisplayLocale(), { timeZoneName: 'short' }).formatToParts(date);
    return parts.find((part) => part.type === 'timeZoneName')?.value;
  }

  /**
   * Returns the locale used for all opening-time formatting.
   * @returns Active language, falling back to the default language and finally `"de-DE"`.
   */
  private getDisplayLocale(): string {
    return this.translateService.getCurrentLang() ?? this.translateService.getFallbackLang() ?? 'de-DE';
  }

  /**
   * Whether times are rendered in the visitor's timezone and therefore need a short TZ label.
   * @returns True only in visitor-TZ mode with a store timezone that differs from the visitor's.
   */
  private showsTimeZoneLabel(): boolean {
    if (!this.environmentService.useVisitorTimeZone) return false;
    const storeTz = this.storeTimeZone();
    return !!storeTz && storeTz !== this.getUserTimeZone();
  }

  /**
   * Extracts the time-of-day (minutes since midnight) from a UTC ISO timestamp,
   * interpreted in the given IANA timezone.
   * Example: "2026-07-15T06:00:00Z" + "Europe/Berlin" (summer DST) → 480 (= 08:00).
   * @param utcIso UTC ISO timestamp.
   * @param tz IANA timezone name.
   * @returns Minutes since midnight in the target timezone, or -1 when invalid.
   */
  private getTimeInMinutesInZone(utcIso: string | undefined, tz: string): number {
    if (!utcIso) return -1;
    const date = new Date(utcIso);
    if (!DateService.isValidDate(date)) return -1;
    return this.getMinutesOfDayInZone(date, tz);
  }

  /**
   * Returns the minutes-since-midnight value of a `Date` interpreted in a given IANA timezone.
   * @param date Absolute instant in time.
   * @param tz IANA timezone name.
   * @returns Minutes since midnight in that timezone, or -1 when extraction fails.
   */
  private getMinutesOfDayInZone(date: Date, tz: string): number {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).formatToParts(date);
    const hh = Number(parts.find((p) => p.type === 'hour')?.value);
    const mm = Number(parts.find((p) => p.type === 'minute')?.value);
    if (isNaN(hh) || isNaN(mm)) return -1;
    // `hour12: false` in en-US returns "24" at midnight — normalize to 0.
    const hours = hh === 24 ? 0 : hh;
    return hours * 60 + mm;
  }

  /**
   * Returns the weekday (0 = Sunday, 6 = Saturday) of a date evaluated in the given timezone.
   * @param date Absolute instant in time.
   * @param tz IANA timezone name.
   * @returns Weekday index, or -1 when extraction fails.
   */
  private getWeekdayInZone(date: Date, tz: string): number {
    const weekdayStr = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short' }).format(date);
    const map: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    return map[weekdayStr] ?? -1;
  }

  /**
   * Returns the calendar year/month/day of a date evaluated in the given timezone.
   * @param date Absolute instant in time.
   * @param tz IANA timezone name.
   * @returns Object with year (full), month (0-based) and day (1-based).
   */
  private getDatePartsInZone(date: Date, tz: string): { year: number; month: number; day: number } {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(date);
    return {
      year: Number(parts.find((p) => p.type === 'year')?.value),
      month: Number(parts.find((p) => p.type === 'month')?.value) - 1,
      day: Number(parts.find((p) => p.type === 'day')?.value)
    };
  }

  /**
   * Resolves the effective timezone for backend-UTC interpretation.
   * Uses the explicitly set store timezone, falling back to the user's browser timezone.
   * @returns IANA timezone name.
   */
  private getEffectiveTimeZone(): string {
    return this.storeTimeZone() ?? this.getUserTimeZone();
  }

  /**
   * Returns the user's IANA timezone as resolved by the browser runtime.
   * @returns IANA timezone name.
   */
  private getUserTimeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
}
